import { Link } from "react-router-dom";
import { PublicLayout } from "@/components/PublicLayout";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PROMISES } from "@/data/privacyPromises";

const FAQ = [
  ["who", "Who can read my journal?", "Only you, while signed in to your account. Entries are stored securely so they sync across your devices, but they're never shown to supporters, therapists (unless you include them in a share), or our staff."],
  ["supporters", "What exactly do my supporters see?", "Supporters see the small things you turn on: streaks, hugs, weekly updates and mood forecasts if you enable them. They never see journal text, chat messages, safety plans or individual check-in notes. You can remove a supporter at any time."],
  ["therapist", "How does sharing with my therapist work?", "You create a share link yourself and pick what goes in it. The link needs a 4-digit PIN, locks after too many wrong guesses and expires after 7 days. You can also download a PDF and hand it over yourself."],
  ["ai", "What happens to what I tell Mia, the AI companion?", "Your message is sent securely to our AI provider to write a reply, then saved to your own chat history so you can scroll back. It isn't used for ads or to train AI models, and you can delete your chats."],
  ["staff", "Can Mindful Me staff look at my data?", "No. Our admin tools show totals (like how many check-ins happened today) and account basics needed for support and billing. Your entries are used only to power your own features."],
  ["payments", "What payment details do you keep?", "Checkout happens on PayPal. We store the payment confirmation, amount and the email PayPal gives us for your receipt. We never see or store your card number."],
  ["delete", "How do I export or delete my data?", "Open Settings, then 'Your data'. You can download a full export, clear your tracking data, or permanently delete your account and everything in it."],
  ["crisis", "Is anything ever shared in an emergency?", "No one is contacted automatically. In tough moments the app shows crisis resources like 988 and your own safety plan, and you choose whether to reach out."],
];

const PromiseCard = ({ id, Icon, title, text }) => (
  <div className="mm-card p-6 flex gap-4" data-testid={`privacy-promise-${id}`}>
    <div className="w-11 h-11 shrink-0 rounded-xl mm-gradient-soft flex items-center justify-center text-mm-primary"><Icon size={20} /></div>
    <div>
      <h3 className="font-bold">{title}</h3>
      <p className="text-sm text-mm-muted mt-1 leading-relaxed">{text}</p>
    </div>
  </div>
);

const PrivacyFaq = () => (
  <Accordion type="single" collapsible className="mm-card px-5 mt-6" data-testid="privacy-faq">
    {FAQ.map(([id, q, a]) => (
      <AccordionItem key={id} value={id} className="last:border-b-0" data-testid={`privacy-faq-${id}`}>
        <AccordionTrigger className="text-left font-semibold">{q}</AccordionTrigger>
        <AccordionContent className="text-mm-muted leading-relaxed">{a}</AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

export default function PrivacyPromise() {
  usePageMeta({ title: "Our Privacy Promise — Mindful Me", description: "How Mindful Me keeps your journal, moods and check-ins private: never sold, never shown to supporters, and always yours to export or delete.", path: "/privacy-promise" });
  return (
    <PublicLayout>
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pt-16 mm-stagger" data-testid="privacy-promise-page">
        <p className="text-mm-primary font-semibold text-sm uppercase tracking-wider">Privacy promise</p>
        <h1 className="text-4xl sm:text-5xl font-extrabold mt-2 max-w-3xl">Your feelings stay yours.</h1>
        <p className="text-mm-muted mt-4 max-w-2xl leading-relaxed">Mindful Me is a place for your most honest thoughts. Here's, in plain words, how we keep them private.</p>
        <div className="grid sm:grid-cols-2 gap-4 mt-10">
          {PROMISES.map(([id, Icon, title, text]) => <PromiseCard key={id} id={id} Icon={Icon} title={title} text={text} />)}
        </div>
        <h2 className="text-lg font-bold mt-16">Questions people ask</h2>
        <PrivacyFaq />
        <p className="text-sm text-mm-muted mt-8">
          Want the full details? Read our <Link to="/legal" className="text-mm-primary underline" data-testid="privacy-promise-legal-link">privacy policy</Link> or <Link to="/contact-us" className="text-mm-primary underline" data-testid="privacy-promise-contact-link">ask us anything</Link>.
        </p>
      </section>
    </PublicLayout>
  );
}
